import React, { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { validateCart, getCart } from '../services/cartApi';
import { createOrder, createRevolutOrder, createGuestRevolutOrder } from '../services/orderApi';
import RevolutCheckout from '@revolut/checkout';
import { validatePromo } from '../services/promoApi';
import { getTranslatedProduct } from '../utils/translationUtils';
import { useCurrency } from '../context/CurrencyContext';

const CustomInput = ({
  type,
  placeholder,
  value,
  onChange,
  className = '',
}) => (
  <input
    type={type || 'text'}
    placeholder={placeholder}
    value={value}
    onChange={onChange}
    className={`bg-[#9797971A] w-full h-[46px] px-6 outline-none placeholder:text-[#808080] text-[14px] font-normal leading-[22px] tracking-[-0.64px] ${className}`}
  />
);

const Checkout = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const { formatPrice } = useCurrency();
  const [couponCode, setCouponCode] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // { code, value, discount }
  const [couponMsg, setCouponMsg] = useState('');

  const [showCouponDropdown, setShowCouponDropdown] = useState(false);
  const [email, setEmail] = useState('');

  useEffect(() => {
    if (user?.email) {
      setEmail(user.email);
    }
  }, [user]);
  const [createAccount, setCreateAccount] = useState(false);
  const [receiveUpdates, setReceiveUpdates] = useState(false);
  const [country, setCountry] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [address, setAddress] = useState('');
  const [city, setCity] = useState('');
  const [state, setState] = useState('');
  const [postalCode, setPostalCode] = useState('');
  const [phone, setPhone] = useState('');
  const [useSameBilling, setUseSameBilling] = useState(false);
  const [showApartment, setShowApartment] = useState(false);
  const [apartment, setApartment] = useState('');
  const [payerName] = useState('');
  const [cardNumber] = useState('');
  const [expiryDate] = useState('');
  const [cvv] = useState('');
  const [cartItems, setCartItems] = useState([]);
  const [placing, setPlacing] = useState(false);
  const [modal, setModal] = useState({
    open: false,
    success: false,
    message: '',
    orderNumber: '',
  });
  const [paymentMethod] = useState('revolut'); // 'revolut' or 'card'
  const [revolutInstance, setRevolutInstance] = useState(null);

  useEffect(() => {
    const token =
      typeof localStorage !== 'undefined'
        ? localStorage.getItem('token')
        : null;
    if (token) {
      getCart()
        .then((cart) => {
          const mapped = (cart?.items || []).map((it) => ({
            itemId: it._id,
            id: it.product?._id || it.product,
            name: it.product?.name || 'Product',
            translations: it.product?.translations,
            image:
              (it.product?.images && it.product.images[0]) ||
              '/assets/product-image.webp',
            price: it.product?.pricing?.regularPrice ?? it.product?.price ?? 0,
            quantity: it.quantity ?? 1,
            color: it.color,
            size: it.size,
          }));
          setCartItems(mapped);
        })
        .catch(() => {
          const stored = JSON.parse(localStorage.getItem('cart') || '[]');
          setCartItems(stored);
        });
    } else {
      const stored = JSON.parse(localStorage.getItem('cart') || '[]');
      setCartItems(stored);
    }

    // Initialize Revolut Checkout
    const initRevolut = async () => {
      try {
        const revolutCheckout = RevolutCheckout({
          locale: 'en', // or use i18n.language
          mode: 'sandbox', // Change to 'prod' for production
        });
        setRevolutInstance(revolutCheckout);
      } catch (error) {
        console.error('Failed to initialize Revolut:', error);
      }
    };

    initRevolut();
  }, []);

  const updateQuantity = (id, newQuantity) => {
    if (newQuantity < 1) return;
    setCartItems((items) => {
      const updated = items.map((item) =>
        item.id === id ? { ...item, quantity: newQuantity } : item
      );
      localStorage.setItem('cart', JSON.stringify(updated));
      return updated;
    });
  };

  const removeItem = (id) => {
    setCartItems((items) => {
      const updated = items.filter((item) => item.id !== id);
      localStorage.setItem('cart', JSON.stringify(updated));
      return updated;
    });
  };

  const subtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );
  const discount = appliedCoupon
    ? Math.min(subtotal, Number(appliedCoupon.discount ?? 0))
    : 0;
  const delivery = 0;
  const total = Math.max(0, subtotal - discount + delivery);

  return (
    <>
      <div className="mt-[78px] px-4 sm:px-6 lg:px-8">
        <div className="max-w-[1244px] mx-auto pt-8 sm:pt-[52px] pb-[166px] sm:pb-[100px] flex items-start md:flex-row flex-col-reverse justify-between gap-20 md:gap-6 lg:gap-10">
          {/* Left Side - Checkout Form */}
          <div className="w-full max-w-[800px]">
            {/* Contact Information Section - Hidden if logged in */}
            {!user && (
              <div className="">
                <h2 className="text-[18px] leading-[20px] font-semibold text-black mb-3">
                  {t('checkout.contactInfo')}
                </h2>
                <p className="text-[#000000CC] text-[12px] leading-[13px] font-normal">
                  {t('checkout.contactDescription')}
                </p>

                <div className="my-6 flex flex-col gap-3 items-start w-full">
                  <CustomInput
                    type="email"
                    placeholder={t('checkout.emailPlaceholder')}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                  />
                  <p className="text-[#000000CC] text-[14px] leading-[15px] font-normal">
                    {t('checkout.guestCheckout')}
                  </p>
                </div>

                <div className="space-y-2">
                  {/* <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={createAccount}
                      onChange={(e) => setCreateAccount(e.target.checked)}
                      className="min-w-6 h-6 border border-[#AFAFAF]"
                    />
                    <span className="text-[14px] leading-[15px] font-normal text-[#000000CC]">
                      {t('checkout.createAccount')}
                    </span>
                  </label> */}
                  <label className="flex items-center gap-2.5 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={receiveUpdates}
                      onChange={(e) => setReceiveUpdates(e.target.checked)}
                      className="min-w-6 h-6 border border-[#AFAFAF]"
                    />
                    <span className="text-[14px] leading-[15px] font-normal text-[#000000CC]">
                      {t('checkout.receiveUpdates')}
                    </span>
                  </label>
                </div>
              </div>
            )}

            {/* Shipping Address Section */}
            <div className="mt-6">
              <h2 className="text-[18px] leading-[20px] font-semibold text-black mb-3">
                {t('checkout.shippingAddress')}
              </h2>
              <p className="text-[#000000CC] text-[14px] leading-[15px] font-normal mb-6">
                {t('checkout.shippingDescription')}
              </p>
              <div className="w-full space-y-6">
                <CustomInput
                  placeholder={t('checkout.countryPlaceholder')}
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                />
                <div className="grid grid-cols-2 gap-4">
                  <CustomInput
                    placeholder={t('checkout.firstName')}
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                  />
                  <CustomInput
                    placeholder={t('checkout.lastName')}
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                  />
                </div>
                <div className="flex items-start flex-col gap-3">
                  <CustomInput
                    placeholder={t('checkout.address')}
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                  />
                  {!showApartment ? (
                    <button
                      onClick={() => setShowApartment(true)}
                      className="text-[#000000CC] text-[12px] leading-[13px] font-normal hover:text-black transition-colors duration-200"
                    >
                      {t('checkout.apartmentNote')}
                    </button>
                  ) : (
                    <div className="space-y-2 w-full">
                      <CustomInput
                        placeholder={t('checkout.apartmentNote')}
                        value={apartment}
                        onChange={(e) => setApartment(e.target.value)}
                      />
                      <button
                        onClick={() => {
                          setShowApartment(false);
                          setApartment('');
                        }}
                        className="text-[#000000CC] text-[12px] leading-[13px] font-normal hover:text-black transition-colors duration-200"
                      >
                        - {t('checkout.apartmentNote')}
                      </button>
                    </div>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <CustomInput
                    placeholder={t('checkout.city')}
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                  <CustomInput
                    placeholder={t('checkout.state')}
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <CustomInput
                    placeholder={t('checkout.postalCode')}
                    value={postalCode}
                    onChange={(e) => setPostalCode(e.target.value)}
                  />
                  <CustomInput
                    placeholder={t('checkout.phone')}
                    value={phone}
                    onChange={(e) => setPhone(e.target.value)}
                  />
                </div>
                <label className="flex items-center gap-2.5 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={useSameBilling}
                    onChange={(e) => setUseSameBilling(e.target.checked)}
                    className="min-w-6 h-6"
                  />
                  <span className="text-[14px] leading-[15px] font-normal text-[#000000CC]">
                    {t('checkout.sameAsShipping')}
                  </span>
                </label>
              </div>
            </div>

            {/* Payment Options Section */}
            {/* <div className='mt-6'>
            <h2 className='text-[18px] leading-[20px] font-semibold text-black mb-3'>{t('checkout.paymentOptions')}</h2>
            <p className='text-[#000000CC] text-[14px] leading-[15px] font-normal mb-6'>
              {t('checkout.paymentDescription')}
            </p>
            <div className='flex flex-col items-start gap-3 w-full mb-6'>
              <CustomInput
                placeholder={t('checkout.payerName')}
                value={payerName}
                onChange={(e) => setPayerName(e.target.value)}
              />
              <CustomInput
                placeholder={t('checkout.cardNumber')}
                value={cardNumber}
                onChange={(e) => setCardNumber(e.target.value)}
              />
            </div>
            <div className='grid grid-cols-2 gap-4'>
              <CustomInput
                placeholder={t('checkout.expiryDate')}
                value={expiryDate}
                onChange={(e) => setExpiryDate(e.target.value)}
              />
              <CustomInput
                placeholder={t('checkout.cvv')}
                value={cvv}
                onChange={(e) => setCvv(e.target.value)}
              />
            </div>
            <p className='text-[#000000CC] text-[12px] leading-[13px] font-normal mt-2 mb-6'>
              {t('checkout.paymentTerms')}
            </p>
          </div> */}

            {/* Place Order Button */}
            <button
              className="mt-6 w-full bg-black text-white text-[13px] font-medium leading-[14px] h-[54px] disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={placing}
              onClick={async () => {
                const token =
                  typeof localStorage !== 'undefined'
                    ? localStorage.getItem('token')
                    : null;
                const isGuest = !token;

                // For guest users, validate email
                if (isGuest && !email) {
                  setModal({
                    open: true,
                    success: false,
                    message: t('checkout.emailRequired') || 'Email is required for guest checkout',
                    orderNumber: '',
                  });
                  return;
                }

                // Validate required fields
                if (
                  !firstName ||
                  !lastName ||
                  !address ||
                  !city ||
                  !country ||
                  !phone
                ) {
                  setModal({
                    open: true,
                    success: false,
                    message: t('checkout.fillShippingInfo'),
                    orderNumber: '',
                  });
                  return;
                }

                try {
                  setPlacing(true);

                  // Only validate cart for logged-in users
                  if (!isGuest) {
                    await validateCart();
                  }

                  const shippingAddress = {
                    firstName,
                    lastName,
                    address1: address,
                    address2: apartment || undefined,
                    city,
                    state,
                    zipCode: postalCode,
                    country,
                    phone,
                  };
                  const billingAddress = useSameBilling
                    ? shippingAddress
                    : shippingAddress;

                  if (paymentMethod === 'revolut') {
                    let response;

                    if (isGuest) {
                      // Guest checkout flow
                      const orderData = {
                        email,
                        cartItems, // Send localStorage cart items
                        shippingAddress,
                        billingAddress,
                        notes: `Guest order - Email: ${email}${appliedCoupon ? ` | Coupon: ${appliedCoupon.code} (${appliedCoupon.value}%)` : ''}`,
                      };

                      response = await createGuestRevolutOrder(orderData);
                    } else {
                      // Logged-in user checkout flow
                      const orderData = {
                        shippingAddress,
                        billingAddress,
                        notes: `Email: ${email}${appliedCoupon ? ` | Coupon: ${appliedCoupon.code} (${appliedCoupon.value}%)` : ''}`,
                      };

                      response = await createRevolutOrder(orderData);
                    }

                    if (response.success && response.data.revolutOrder) {
                      const { revolutOrder } = response.data;

                      // Redirect to Revolut checkout
                      if (revolutOrder.checkout_url) {
                        if (isGuest) {
                          try {
                            const orderNum = response.data?.order?.orderNumber;
                            console.log('Checkout Redirect - Processing Guest Order:', orderNum, response.data);

                            if (orderNum) {
                              const guestOrders = JSON.parse(localStorage.getItem('guest_orders') || '[]');
                              if (!guestOrders.includes(orderNum)) {
                                guestOrders.push(orderNum);
                                localStorage.setItem('guest_orders', JSON.stringify(guestOrders));
                                console.log('Saved guest order to LS:', guestOrders);
                              }
                            } else {
                              console.error('MISSING ORDER NUMBER IN RESPONSE:', response);
                            }
                          } catch (err) {
                            console.error('Error saving guest order:', err);
                          }
                        }
                        window.location.href = revolutOrder.checkout_url;
                      } else if (revolutInstance) {
                        // Use Revolut SDK
                        revolutInstance.payWithPopup({
                          token: revolutOrder.public_id,
                          onSuccess() {
                            // Clear cart on success
                            localStorage.removeItem('cart');
                            setCartItems([]);

                            if (isGuest) {
                              const guestOrders = JSON.parse(localStorage.getItem('guest_orders') || '[]');
                              if (!guestOrders.includes(response.data.order.orderNumber)) {
                                guestOrders.push(response.data.order.orderNumber);
                                localStorage.setItem('guest_orders', JSON.stringify(guestOrders));
                              }
                            }

                            setModal({
                              open: true,
                              success: true,
                              message: t('checkout.paymentCompleted'),
                              orderNumber: response.data.order.orderNumber,
                            });
                          },
                          onError(error) {
                            console.error('Revolut payment error:', error);
                            setModal({
                              open: true,
                              success: false,
                              message: t('checkout.paymentFailed'),
                              orderNumber: '',
                            });
                          },
                          onCancel() {
                            setModal({
                              open: true,
                              success: false,
                              message: t('checkout.paymentCancelled'),
                              orderNumber: '',
                            });
                          },
                        });
                      } else {
                        throw new Error('Revolut checkout not available');
                      }
                    } else {
                      throw new Error('Failed to create Revolut order');
                    }
                  } else {
                    // Handle traditional card payment (only for logged-in users)
                    if (isGuest) {
                      setModal({
                        open: true,
                        success: false,
                        message: 'Card payment is only available for registered users. Please use Revolut payment or create an account.',
                        orderNumber: '',
                      });
                      return;
                    }

                    if (!payerName || !cardNumber || !expiryDate || !cvv) {
                      setModal({
                        open: true,
                        success: false,
                        message: t('checkout.fillCardDetails'),
                        orderNumber: '',
                      });
                      return;
                    }

                    const payment = {
                      method: 'credit_card',
                      cardDetails: {
                        name: payerName,
                        number: cardNumber,
                        expiry: expiryDate,
                        cvv: cvv,
                      },
                    };

                    const order = await createOrder({
                      shippingAddress,
                      billingAddress,
                      payment,
                    });
                    const orderNumber =
                      order?.data?.orderNumber || order?.orderNumber || '';

                    // Clear cart on success
                    localStorage.removeItem('cart');
                    setCartItems([]);

                    setModal({
                      open: true,
                      success: true,
                      message: t('checkout.orderSuccess'),
                      orderNumber,
                    });
                  }
                } catch (e) {
                  console.error('Order creation failed', e);
                  setModal({
                    open: true,
                    success: false,
                    message: e.message || t('checkout.orderFailedGeneric'),
                    orderNumber: '',
                  });
                } finally {
                  setPlacing(false);
                }
              }}
            >
              {placing
                ? t('checkout.processing')
                : paymentMethod === 'revolut'
                  ? t('checkout.payWithRevolut')
                  : t('checkout.placeOrder')}
            </button>
          </div>

          {/* Right Side */}
          <div className="md:border border-[#AFAFAF] md:rounded-xl md:p-5 md:max-w-[300px] lg:max-w-[404px] w-full md:sticky top-20">
            <h2 className="text-black text-[16px] font-semibold leading-[22px] mb-6 md:block hidden">
              {t('checkout.cartTotal')}
            </h2>
            <h2 className="text-black text-[16px] font-semibold leading-[22px] mb-6 md:hidden">
              {t('checkout.cartTotal')}
            </h2>
            {/* Mobile design */}
            <div className="w-full mb-6 md:hidden">
              {cartItems.length > 0 ? (
                cartItems.map((item) => (
                  <div
                    key={item.id}
                    className="w-full border-b border-[#AFAFAF] pb-6"
                  >
                    <div className="max-w-[684px] w-full flex items-start justify-between gap-2">
                      <div className="flex items-center gap-4">
                        <div className="w-[119px] h-[119px] bg-[#D9D9D9] flex items-center justify-center overflow-hidden p-1 rounded">
                          <img
                            src={item.image}
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="space-y-4">
                          <h2 className="text-black text-[20px] leading-[27px] font-semibold">
                            {getTranslatedProduct(item, 'name', i18n.language)}
                          </h2>
                          <div className="border border-[#AFAFAF] rounded-[2px] p-2 flex items-center justify-between gap-[30px]">
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity - 1)
                              }
                              className="text-black text-[18px] leading-[25px] font-medium hover:bg-gray-100 rounded"
                            >
                              <svg
                                width="6"
                                height="2"
                                viewBox="0 0 6 2"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M0.666 1.986V0.366H5.13V1.986H0.666Z"
                                  fill="black"
                                />
                              </svg>
                            </button>
                            <span className="text-black text-[18px] leading-[25px] font-medium">
                              {item.quantity}
                            </span>
                            <button
                              onClick={() =>
                                updateQuantity(item.id, item.quantity + 1)
                              }
                              className="text-black text-[18px] leading-[25px] font-medium hover:bg-gray-100 rounded"
                            >
                              <svg
                                width="10"
                                height="9"
                                viewBox="0 0 10 9"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M5.868 3.926H9.396V5.384H5.868V9.002H4.392V5.384H0.864V3.926H4.392V0.289999H5.868V3.926Z"
                                  fill="black"
                                />
                              </svg>
                            </button>
                          </div>
                          <button
                            onClick={() => removeItem(item.id)}
                            className="text-black text-[14px] leading-[19px] font-normal underline hover:text-gray-600"
                          >
                            {t('cart.removeItem')}
                          </button>
                        </div>
                      </div>
                      <h2 className="text-black text-[18px] leading-[25px] font-medium">
                        {formatPrice(item.price * item.quantity)}
                      </h2>
                    </div>
                  </div>
                ))
              ) : (
                <div className="w-full border-b border-[#AFAFAF] pb-6 text-center">
                  <div className="max-w-[684px] w-full flex flex-col items-center justify-center gap-4">
                    <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                      <svg
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M7 4V2C7 1.45 7.45 1 8 1H16C16.55 1 17 1.45 17 2V4H20C20.55 4 21 4.45 21 5S20.55 6 20 6H19V19C19 20.1 18.1 21 17 21H7C5.9 21 5 20.1 5 19V6H4C3.45 6 3 5.55 3 5S3.45 4 4 4H7ZM9 3V4H15V3H9ZM7 6V19H17V6H7Z"
                          fill="#9CA3AF"
                        />
                      </svg>
                    </div>
                    <h3 className="text-gray-600 text-[18px] leading-[25px] font-semibold">
                      {t('cart.empty.title')}
                    </h3>
                    <p className="text-gray-500 text-[14px] leading-[20px] font-normal max-w-md">
                      {t('cart.empty.subtitle')}
                    </p>
                    <button className="mt-4 bg-black text-white px-10 py-5 uppercase text-[13px] leading-[14px] font-medium">
                      {t('cart.empty.cta')}
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Coupon Section */}
            <button className="px-4 w-full flex justify-between items-center flex-col py-[17px] border-y border-[#AFAFAF]">
              <div
                className="w-full flex items-center justify-between gap-4"
                onClick={() => setShowCouponDropdown(!showCouponDropdown)}
              >
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  {t('cart.coupon.add')}
                </span>
                <div
                  onClick={() => setShowCouponDropdown(!showCouponDropdown)}
                  className="flex items-center gap-1"
                >
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 12 12"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M5.46967 9.53033C5.76256 9.82322 6.23744 9.82322 6.53033 9.53033L11.3033 4.75736C11.5962 4.46447 11.5962 3.98959 11.3033 3.6967C11.0104 3.40381 10.5355 3.40381 10.2426 3.6967L6 7.93934L1.75736 3.6967C1.46447 3.40381 0.989593 3.40381 0.696699 3.6967C0.403806 3.98959 0.403806 4.46447 0.696699 4.75736L5.46967 9.53033ZM6 8H5.25V9H6H6.75V8H6Z"
                      fill="black"
                    />
                  </svg>
                </div>
              </div>
              {showCouponDropdown && (
                <div className="mt-5 w-full">
                  <input
                    type="text"
                    placeholder={t('cart.coupon.placeholder')}
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value)}
                    className="w-full border border-[#AFAFAF] rounded px-3 py-3 text-[13px] outline-none"
                  />
                  <div className="mt-3 flex gap-2">
                    {!appliedCoupon ? (
                      <button
                        type="button"
                        onClick={async () => {
                          const code = couponCode.trim().toUpperCase();
                          if (!code) {
                            setCouponMsg('Enter a coupon code');
                            return;
                          }
                          try {
                            const res = await validatePromo(code, subtotal);
                            const data = res?.data || res;
                            const val = Number(data?.value ?? 0);
                            const disc = Number(data?.discount ?? 0);
                            if (!val && !disc)
                              throw new Error('Invalid coupon');
                            setAppliedCoupon({
                              code,
                              value: val,
                              discount: disc,
                            });
                            setCouponMsg('Coupon applied');
                          } catch (e) {
                            setAppliedCoupon(null);
                            setCouponMsg(e.message || 'Invalid coupon');
                          }
                        }}
                        className="bg-black text-white px-4 py-2 rounded text-sm"
                      >
                        Apply
                      </button>
                    ) : (
                      <button
                        type="button"
                        onClick={() => {
                          setAppliedCoupon(null);
                          setCouponMsg('Coupon removed');
                        }}
                        className="bg-gray-200 text-black px-4 py-2 rounded text-sm"
                      >
                        Remove
                      </button>
                    )}
                    {couponMsg && (
                      <span
                        className={`text-sm ${appliedCoupon ? 'text-green-600' : 'text-red-600'}`}
                      >
                        {couponMsg}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </button>

            {/* Subtotal */}
            <div className="flex justify-between items-center mt-6 px-4 md:px-0">
              <span className="text-black text-[16px] leading-[22px] font-normal">
                {t('checkout.subtotal')}
              </span>
              <span className="text-black text-[16px] leading-[22px] font-normal">
                {formatPrice(subtotal)}
              </span>
            </div>
            {/* Discount */}
            {appliedCoupon && (
              <div className="flex justify-between items-center mt-[10px] px-4 md:px-0">
                <span className="text-black text-[16px] leading-[22px] font-normal">
                  Discount ({appliedCoupon.value}%)
                </span>
                <span className="text-[#15A85D] text-[16px] leading-[22px] font-normal">
                  - {formatPrice(discount)}
                </span>
              </div>
            )}
            {/* Delivery */}
            <div className="flex justify-between items-center mt-[19px] px-4 md:px-0">
              <span className="text-black text-[16px] leading-[22px] font-normal">
                {t('checkout.delivery')}
              </span>
              <span className="text-black text-[12px] leading-[16px] font-normal">
                {t('checkout.enterAddress')}
              </span>
            </div>
            <div className="w-full flex justify-between items-center pt-3.5 border-t border-[#AFAFAF] mt-6">
              <span className="text-black text-[16px] leading-[22px] font-normal">
                {t('checkout.total')}
              </span>
              <span className="text-black text-[16px] leading-[22px] font-normal">
                {formatPrice(total)}
              </span>
            </div>
          </div>
        </div>
      </div>
      {/* Placing overlay */}
      {placing && (
        <div className="fixed inset-0 z-[1000] bg-black/50 flex items-center justify-center">
          <div className="bg-white rounded-lg px-6 py-4 shadow">
            {t('checkout.processingOrder')}
          </div>
        </div>
      )}

      {/* Result modal */}
      {modal.open && (
        <div className="fixed inset-0 z-[1001] bg-black/50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 text-center">
            <h3
              className={`text-xl font-semibold ${modal.success ? 'text-green-700' : 'text-red-700'}`}
            >
              {modal.success
                ? t('checkout.orderPlaced')
                : t('checkout.orderFailed')}
            </h3>
            <p className="mt-3 text-gray-700">{modal.message}</p>
            {modal.success && modal.orderNumber && (
              <p className="mt-2 text-sm text-gray-500">
                Order Number:{' '}
                <span className="font-mono">{modal.orderNumber}</span>
              </p>
            )}
            <div className="mt-6 flex gap-3 justify-center">
              {modal.success && modal.orderNumber ? (
                <button
                  onClick={() => {
                    setModal({
                      open: false,
                      success: false,
                      message: '',
                      orderNumber: '',
                    });
                    navigate(
                      `/${i18n.language}/track-your-order?order=${encodeURIComponent(modal.orderNumber)}`
                    );
                  }}
                  className="bg-black text-white px-5 py-2 rounded"
                >
                  {t('checkout.trackOrder')}
                </button>
              ) : (
                <button
                  onClick={() =>
                    setModal({
                      open: false,
                      success: false,
                      message: '',
                      orderNumber: '',
                    })
                  }
                  className="bg-gray-900 text-white px-5 py-2 rounded"
                >
                  {t('checkout.close')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Checkout;
